import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { isNil } from 'ramda';

import { productFamilyCodes } from 'types/productFamilyCodes';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { upgradePriceBreakdownShape } from 'constants/propTypes';

import { isFareCellDisabled } from '../SearchResultsTable.utils';
import Cell from './Cell';
import CellContent from './CellContent';
import UpgradePriceTooltip from './UpgradePriceTooltip';
import {
  StyledButton,
  StyledProductFammilyMismatch,
  StyledLowestFare,
  StyledPromoFare,
  StyledSeatsLeft,
  StyledUpgradePrice,
  StyledUpgradePriceAnchor
} from './ClickableCell.styles';

class ClickableCell extends Component {
  shouldComponentUpdate(nextProps) {
    return JSON.stringify(this.props) !== JSON.stringify(nextProps);
  }

  render() {
    const {
      isSelected,
      canBeBooked,
      displayPrice,
      hasAvailableFares,
      cappedTrainFreeSeats,
      onClick,
      innerBorderColor,
      innerBorderWidth = 1,
      isTimeRestricted,
      id,
      isExistingTrain,
      currency,
      isDifferentProductFamily,
      fareProductFamilyCode,
      loyaltyPoints,
      isLowestFare,
      promoDiscount,
      seatsLeft = 0,
      classOfService,
      hasAnimalsSoldOut,
      isExchangingSingleSNLegOfASummatedFare,
      isExchangingSNCBLeg,
      fromSNSummatedToSNSummated,
      hasLuggageSoldOut,
      upgradePriceDifference,
      upgradePriceBreakdown
    } = this.props;

    const disabled = isFareCellDisabled(this.props);
    const upgradePriceTooltipId = `${id}-upgrade-price`;
    const upgradePrice = !isNil(upgradePriceDifference) && (
      <FormattedCurrency currency={currency} amount={upgradePriceDifference} hideEmptyDecimals={Number.isInteger(upgradePriceDifference)} />
    );

    return (
      <>
        <Cell innerBorderColor={innerBorderColor} innerBorderWidth={innerBorderWidth} id={id}>
          <StyledButton
            id="button"
            onClick={disabled ? null : onClick}
            selected={isSelected}
            disabled={disabled}
            classOfService={classOfService}
            data-selected={isSelected}
            data-disabled={disabled}
            isExistingTrain={isExistingTrain}
          >
            {(isExchangingSingleSNLegOfASummatedFare || isExchangingSNCBLeg || fromSNSummatedToSNSummated) &&
            displayPrice < 0 &&
            !disabled ? (
              <StyledLowestFare>
                <FormattedMessage id="payment.refund" />
              </StyledLowestFare>
            ) : promoDiscount && !disabled ? (
              <StyledPromoFare data-cy="promo-fare">
                <FormattedMessage id="searchResults.promoDiscount" values={{ value: promoDiscount }} />
              </StyledPromoFare>
            ) : isLowestFare && !disabled ? (
              <StyledLowestFare>
                <FormattedMessage id="searchResults.lowestFare" />
              </StyledLowestFare>
            ) : (
              <></>
            )}

            <CellContent
              isTimeRestricted={isTimeRestricted}
              hasAvailableFares={hasAvailableFares}
              canBeBooked={canBeBooked}
              cappedTrainFreeSeats={cappedTrainFreeSeats}
              currency={currency}
              displayPrice={displayPrice}
              isExistingTrain={isExistingTrain}
              loyaltyPoints={loyaltyPoints}
              hasAnimalsSoldOut={hasAnimalsSoldOut}
              isExchangingSingleSNLegOfASummatedFare={isExchangingSingleSNLegOfASummatedFare}
              isExchangingSNCBLeg={isExchangingSNCBLeg}
              fromSNSummatedToSNSummated={fromSNSummatedToSNSummated}
              hasLuggageSoldOut={hasLuggageSoldOut}
            >
              {upgradePrice && (
                <StyledUpgradePriceAnchor data-for={upgradePriceTooltipId} data-tip onClick={(event) => event.stopPropagation()}>
                  <StyledUpgradePrice data-cy="upgrade-price-difference">
                    <FormattedMessage id="searchResults.upgradePriceDifference" values={{ price: upgradePrice }} />
                  </StyledUpgradePrice>
                </StyledUpgradePriceAnchor>
              )}
            </CellContent>

            {isDifferentProductFamily && !disabled && (
              <StyledProductFammilyMismatch hasFewSeatsLeft={seatsLeft > 0 && !disabled} data-cy="fare-indicator">
                {fareProductFamilyCode === productFamilyCodes.PUB ? 'Public' : fareProductFamilyCode}
              </StyledProductFammilyMismatch>
            )}

            {seatsLeft > 0 && !disabled && (
              <StyledSeatsLeft>
                <FormattedMessage id="searchResults.seatsLeft" values={{ seatsLeft }} />
              </StyledSeatsLeft>
            )}
          </StyledButton>
          {upgradePrice && upgradePriceBreakdown && (
            <UpgradePriceTooltip id={upgradePriceTooltipId} currency={currency} breakdown={upgradePriceBreakdown} />
          )}
        </Cell>
      </>
    );
  }
}

ClickableCell.propTypes = {
  isSelected: PropTypes.bool,
  displayPrice: PropTypes.number,
  loyaltyPoints: PropTypes.number,
  canBeBooked: PropTypes.bool,
  hasAvailableFares: PropTypes.bool,
  cappedTrainFreeSeats: PropTypes.bool,
  onClick: PropTypes.func,
  innerBorderWidth: PropTypes.number,
  innerBorderColor: PropTypes.string,
  isTimeRestricted: PropTypes.bool,
  id: PropTypes.string,
  currency: PropTypes.string,
  isExistingTrain: PropTypes.bool,
  isDifferentProductFamily: PropTypes.bool,
  isLowestFare: PropTypes.bool,
  promoDiscount: PropTypes.number,
  seatsLeft: PropTypes.number,
  fareProductFamilyCode: PropTypes.string,
  classOfService: PropTypes.string,
  hasAnimalsSoldOut: PropTypes.bool,
  isExchangingSingleSNLegOfASummatedFare: PropTypes.bool,
  isExchangingSNCBLeg: PropTypes.bool,
  fromSNSummatedToSNSummated: PropTypes.bool,
  hasLuggageSoldOut: PropTypes.bool,
  upgradePriceDifference: PropTypes.number,
  upgradePriceBreakdown: upgradePriceBreakdownShape
};

export default ClickableCell;
